import { getDefaultConfig } from '@rainbow-me/rainbowkit'
import { http } from 'wagmi'
import { mainnet, sepolia, base, arbitrum, optimism, polygon, hardhat } from 'wagmi/chains'
import { CHAIN_ID, CHAIN_MISMATCH, usesLoopback } from '@/lib/contracts'

const alchemyKey = import.meta.env.VITE_ALCHEMY_KEY
const wcProjectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || 'horizon-protocol-default'
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

/// Every chain this app knows how to read, keyed the way the generated address book identifies
/// itself. An unknown id falls back to mainnet AND is reported by `CHAIN_MISMATCH` in contracts.js,
/// so the app degrades to one honest pre-launch state rather than to a silently wrong network.
const KNOWN_CHAINS = { 1: mainnet, 11155111: sepolia, 8453: base, 42161: arbitrum, 10: optimism, 137: polygon, 31337: hardhat }

/// **A publicly-served page must never reach for a loopback address.** Deriving the chain from
/// `CHAIN_ID` was right, but selecting `hardhat` on a public host made app.horizon.win try to open
/// `http://127.0.0.1:8545` on the VISITOR's machine, which fires Chrome's local-network-access
/// prompt ("wants to access other apps and services on this device"). That reads as a site probing
/// your LAN, which is exactly what it was doing.
///
/// So `CHAIN_MISMATCH` - a local address book on a public host - falls back to mainnet here. Nothing
/// is readable in that state either way (`IS_LIVE` is false, so every gate is closed), and between
/// two unusable configurations the one that stays off the visitor's local network wins.
const DEPLOY_CHAIN = CHAIN_MISMATCH ? mainnet : (KNOWN_CHAINS[CHAIN_ID] ?? mainnet)

/// Loopback transport only where loopback is reachable - the dev machine. Registering it
/// unconditionally is what let the public build hold a 127.0.0.1 endpoint at all.
const LOCAL_RPC = 'http://127.0.0.1:8545'
const wantsLocal = usesLoopback(CHAIN_ID, window.location.hostname)

/// Alchemy's per-network subdomain, so a testnet deploy gets a real endpoint rather than a mainnet
/// one. The public fallbacks are last resorts: `eth.llamarpc.com` was returning 521s while this was
/// being written, and a hanging RPC is what turned the Vaults page's skeleton into a permanent one.
const ALCHEMY_SUBDOMAIN = { 1: 'eth-mainnet', 11155111: 'eth-sepolia', 8453: 'base-mainnet', 42161: 'arb-mainnet', 10: 'opt-mainnet', 137: 'polygon-mainnet' }
const PUBLIC_RPC = { 1: 'https://eth.llamarpc.com', 11155111: 'https://ethereum-sepolia-rpc.publicnode.com', 8453: 'https://base.llamarpc.com', 42161: 'https://arbitrum.llamarpc.com', 10: 'https://optimism.llamarpc.com', 137: 'https://polygon.llamarpc.com' }

const rpcUrl = alchemyKey && ALCHEMY_SUBDOMAIN[DEPLOY_CHAIN.id]
  ? `https://${ALCHEMY_SUBDOMAIN[DEPLOY_CHAIN.id]}.g.alchemy.com/v2/${alchemyKey}`
  : (PUBLIC_RPC[DEPLOY_CHAIN.id] ?? 'https://eth.llamarpc.com')

export const config = getDefaultConfig({
  appName: 'Horizon Protocol',
  // Absolute URL, not a relative path - shown in WalletConnect's own pairing UI inside a mobile
  // wallet app scanning the QR code, which needs a real reachable address, not window.location.
  appIcon: 'https://app.horizon.win/horizon.png',
  projectId: wcProjectId,
  // Read the chain the ADDRESS BOOK was built for, rather than assuming mainnet.
  //
  // This used to be a flat `[mainnet]` on any non-localhost host, which is how the public site came
  // to ask mainnet for hardhat addresses: `eth_getCode` returns `0x` there, so no read could ever
  // succeed, and each page failed differently (see contracts.js's own CHAIN_MISMATCH note). Pinning
  // mainnet also meant a testnet deploy would reproduce the same bug under a different chain id -
  // the address book would say Sepolia and the app would still be reading Ethereum.
  //
  // Deriving from `CHAIN_ID` makes a public TESTNET deployment work with no further change: point
  // `deployLocal.cjs` at Sepolia, and the generated block carries 11155111, and this reads Sepolia.
  chains: [DEPLOY_CHAIN, ...(wantsLocal && DEPLOY_CHAIN.id !== hardhat.id ? [hardhat] : [])],
  transports: {
    [DEPLOY_CHAIN.id]: http(DEPLOY_CHAIN.id === hardhat.id ? LOCAL_RPC : rpcUrl),
    ...(wantsLocal ? { [hardhat.id]: http(LOCAL_RPC) } : {}),
  },
  pollingInterval: isLocal ? 1_000 : 4_000,
})
